import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Platform,
  Pressable,
  TextInput as RNTextInput,
  View,
  type ColorValue,
} from 'react-native';
import { SearchIcon } from '../../icons/searchIcon';
import { XIcon } from '../../icons/xIcon';
import { borderRadius, colors, spacing, typography } from '../../tokens';
import { Colors } from '../../../../themes/colors';
import { themedStyleSheet } from '../../../../themes/themedStyleSheet';

export interface SearchInputProps {
  /** Current value of the search input (controlled) */
  value: string;

  /** Callback when the text changes */
  onChangeText: (text: string) => void;

  /** Placeholder text */
  placeholder?: string;

  /** Debounce delay in milliseconds (default: 500) */
  debounceTimeMs?: number;

  /**
   * Minimum character length before debounce fires (default: 1).
   * Setting to 0 will fire immediately on every keystroke.
   */
  debounceLength?: number;

  /** Callback fired after the debounce delay with the current value */
  onDebounce?: (value: string) => void;

  /** Auto-focus the input on mount */
  autoFocus?: boolean;

  /** Custom height (default: 32) */
  height?: number;

  /** Custom background color (default: neutral[200]) */
  backgroundColor?: ColorValue;

  /** Custom width */
  width?: number | '100%';

  /** Callback when input loses focus */
  onBlur?: () => void;

  /** Test ID for testing */
  testID?: string;

  /** Hide the search icon on the left (default: false) */
  hideSearchIcon?: boolean;

  /** Hide the clear button on the right (default: false) */
  hideClearButton?: boolean;
}

export const SearchInput = ({
  value,
  onChangeText,
  placeholder = 'Search...',
  debounceTimeMs = 500,
  debounceLength = 1,
  onDebounce,
  autoFocus = false,
  height = 32,
  backgroundColor = Colors.role.inputBackground,
  width,
  onBlur,
  testID,
  hideSearchIcon = false,
  hideClearButton = false,
}: SearchInputProps) => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const onDebounceRef = useRef(onDebounce);
  onDebounceRef.current = onDebounce;
  const [clearHovered, setClearHovered] = useState(false);

  useEffect(() => {
    const handler = onDebounceRef.current;
    if (!handler) return;

    if (value.length === 0) {
      clearTimeout(timeoutRef.current);
      handler('');
      return;
    }

    if (value.length < debounceLength) {
      clearTimeout(timeoutRef.current);
      return;
    }

    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      handler(value);
    }, debounceTimeMs);

    return () => clearTimeout(timeoutRef.current);
  }, [value, debounceTimeMs, debounceLength]);

  const handleClear = useCallback(() => {
    onChangeText('');
    setClearHovered(false);
  }, [onChangeText]);

  const showClear = value.length > 0 && !hideClearButton;

  return (
    <View
      style={[
        styles.container,
        { height, backgroundColor },
        width != null && { width },
      ]}
      testID={testID}
    >
      {!hideSearchIcon && (
        <View style={styles.iconLeft}>
          <SearchIcon size={14} color={Colors.role.textSecondary} />
        </View>
      )}

      <RNTextInput
        value={value}
        onChangeText={onChangeText}
        onBlur={onBlur}
        placeholder={placeholder}
        placeholderTextColor={Colors.role.textDisabled}
        autoFocus={autoFocus}
        cursorColor={colors.orange[500]}
        selectionColor={colors.orange[500]}
        style={styles.input}
      />

      {showClear && (
        <Pressable
          style={[
            styles.clearButton,
            clearHovered && styles.clearButtonHovered,
          ]}
          onPress={handleClear}
          onHoverIn={() => setClearHovered(true)}
          onHoverOut={() => setClearHovered(false)}
          hitSlop={8}
          accessibilityRole="button"
          accessibilityLabel="Clear search"
        >
          <XIcon size={10} color={Colors.role.textStrong} />
        </Pressable>
      )}
    </View>
  );
};

const styles = themedStyleSheet((role) => ({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: borderRadius.lg,
    paddingHorizontal: spacing.sm,
    gap: spacing.xs,
  },
  iconLeft: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  input: {
    flex: 1,
    ...typography.body.small,
    ...Platform.select({
      ios: { lineHeight: undefined },
    }),
    color: role.textPrimary,
    paddingVertical: 0,
    outlineStyle: 'none',
  } as any,
  clearButton: {
    width: 20,
    height: 20,
    borderRadius: borderRadius.sm,
    alignItems: 'center',
    justifyContent: 'center',
  },
  clearButtonHovered: {
    backgroundColor: role.surfaceActive,
  },
}));
